import { memo, useMemo } from "react";
import { buildPianoKeys } from "@/music/chords/pianoChords";
import { getChordNotes, normalizePitchClass } from "@/music/chords/chordParser";

const PianoChordVisualizer = memo(
  ({
    chord,
    activeNotes,
    nextChord,
    timeToNextChord,
    compact = false,
    showLabels = true,
    instrumentMode = "piano",
    variant = "dark",
  }) => {
    const piano = useMemo(() => buildPianoKeys({ startOctave: 3, octaves: 2 }), []);
    const normalizedActiveNotes = useMemo(() => {
      const sourceNotes = Array.isArray(activeNotes) && activeNotes.length ? activeNotes : getChordNotes(chord);
      return new Set(sourceNotes.map(normalizePitchClass).filter(Boolean));
    }, [activeNotes, chord]);

    const activeList = Array.from(normalizedActiveNotes);
    const whiteKeyWidth = `${100 / piano.whiteCount}%`;
    const blackKeyWidth = `${(100 / piano.whiteCount) * 0.62}%`;

    return (
      <div
        className="w-full"
        role="img"
        aria-label={`Piano visual para ${chord || "sin acorde"} en modo ${instrumentMode}`}
      >
        <div className={`mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs ${variant === "light" ? "text-slate-500" : "text-cyan-100/75"}`}>
          <span>{activeList.length ? `Notas iluminadas: ${activeList.join(" / ")}` : "Notas iluminadas: sin acorde detectado"}</span>
          {nextChord && (
            <span>
              Siguiente: <strong className={variant === "light" ? "text-teal-700" : "text-cyan-200"}>{nextChord}</strong>
              {Number.isFinite(timeToNextChord) ? ` en ${timeToNextChord.toFixed(1)}s` : ""}
            </span>
          )}
        </div>

        <div className="steel-scroll-soft overflow-x-auto pb-1">
          <div className={`relative mx-auto min-w-[22rem] max-w-[43rem] ${compact ? "h-[5.6rem] sm:h-[6.2rem]" : "h-28 sm:h-32"}`}>
            <div className="absolute inset-x-0 bottom-0 top-0 flex">
              {piano.whiteKeys.map((key) => {
                const isActive = normalizedActiveNotes.has(key.note);
                return (
                  <div
                    key={key.id}
                    className={`relative flex items-end justify-center rounded-b-md border border-slate-700/50 pb-1.5 transition-all duration-200 ${
                      isActive
                        ? "bg-gradient-to-b from-cyan-100 via-cyan-200 to-violet-200 text-slate-950 shadow-[0_0_28px_rgba(103,232,249,0.45)]"
                        : "bg-gradient-to-b from-slate-100 to-slate-300 text-slate-600"
                    }`}
                    style={{ width: whiteKeyWidth }}
                  >
                    {showLabels && (
                      <span className={`text-[10px] font-bold ${isActive ? "opacity-100" : "opacity-50"}`}>
                        {key.note}
                      </span>
                    )}
                    {isActive && <span className="absolute bottom-6 h-2 w-2 rounded-full bg-cyan-500 shadow-[0_0_18px_rgba(6,182,212,0.9)]" />}
                  </div>
                );
              })}
            </div>

            {piano.blackKeys.map((key) => {
              const isActive = normalizedActiveNotes.has(key.note);
              return (
                <div
                  key={key.id}
                  className={`absolute top-0 z-10 flex h-[62%] -translate-x-1/2 items-end justify-center rounded-b-lg border border-black/50 pb-2 transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-b from-violet-300 via-cyan-300 to-cyan-500 text-slate-950 shadow-[0_0_30px_rgba(167,139,250,0.5)]"
                      : "bg-gradient-to-b from-slate-900 to-black text-slate-300"
                  }`}
                  style={{
                    left: `${key.leftPercent}%`,
                    width: blackKeyWidth,
                  }}
                >
                  {showLabels && (
                    <span className={`text-[9px] font-bold ${isActive ? "opacity-100" : "opacity-50"}`}>
                      {key.note}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

PianoChordVisualizer.displayName = "PianoChordVisualizer";

export default PianoChordVisualizer;
